(() => {
  // app/scripts/utils/fetchSortedProductCards.js
  async function fetchSortedProductCards(products) {
    const productIDs = products.map(({ ec_item_group_id }) => ec_item_group_id);
    const query = productIDs.map((id) => `id:${id}`).join(" OR ");
    const response = await fetch(`${theme.routes.searchUrl}?view=coveo-search-form&type=product&q=${query}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch product cards: ${response.status} ${response.statusText}`);
    }
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const items = [...doc.querySelectorAll("[data-product-id]")];
    const itemsById = new Map(items.map((item) => [item.dataset.productId, item]));
    const sorted = productIDs.map((id) => itemsById.get(id)).filter(Boolean);
    return sorted.map((el) => el.outerHTML).join("");
  }

  // app/scripts/components/ProductSelector/ProductSelectorSearch.js
  var { debounceTime } = theme;
  var ProductSelectorSearch = class extends HTMLElement {
    _loading = false;
    firstRender = false;
    callbacks = [];
    disabledVariants = /* @__PURE__ */ new Set();
    _renderSeq = 0;
    _nativeProducts = [];
    constructor() {
      super();
      this.selectedItem = null;
      this.prevSearchQuery = "";
      this.focusedIndex = -1;
      this.input = this.querySelector("textarea");
      this.resultsContainer = this.querySelector(".js-results-container");
      this.clearButton = this.querySelector(".js-clear-btn");
      this.debounceExecuteSearch = debounceTime(this.executeSearch.bind(this), 500);
    }
    get platform() {
      return this.dataset.searchPlatform || "search_and_discovery";
    }
    async connectedCallback() {
      if (this.platform === "coveo") {
        const ready = await this.initCoveoEngine();
        if (!ready)
          return;
      }
      this.initInputEvents();
      this.resultsContainer.addEventListener("click", this.handleResultClick.bind(this));
      this.clearButton.addEventListener("click", this.handleClearClick.bind(this));
    }
    async initCoveoEngine() {
      const token = await CoveoUtils.getSearchToken();
      if (!token)
        return false;
      const { loadSearchActions, loadQueryActions, buildSearch } = coveoheadless;
      this.engine = window.configureHeadless(token);
      this.search = buildSearch(this.engine);
      this.search.subscribe(() => {
        if (!this.firstRender) {
          this.firstRender = true;
          return;
        }
        if (this.search.state.isLoading) {
          this.setState("loading");
          return;
        }
        this.renderProducts();
      });
      this.queryActions = loadQueryActions(this.engine);
      this.searchActions = loadSearchActions(this.engine);
      return true;
    }
    initInputEvents() {
      this.input.addEventListener("input", () => {
        this.setInputHeight();
        if (this.input.value.trim()) {
          this.classList.add("is-open");
          this.debounceExecuteSearch();
        } else {
          this.classList.remove("is-results", "is-loading");
          this.resultsContainer.innerHTML = "";
        }
      });
      this.input.addEventListener("focus", () => {
        this.classList.add("is-open");
      });
      this.input.addEventListener("keydown", (event) => {
        switch (event.key) {
          case "ArrowDown":
            event.preventDefault();
            this.handleArrowDown();
            break;
          case "ArrowUp":
            event.preventDefault();
            this.handleArrowUp();
            break;
          case "Enter":
            event.preventDefault();
            this.handleEnterKey();
            break;
        }
      });
      document.addEventListener("click", (event) => {
        if (!this.contains(event.target)) {
          this.classList.remove("is-open");
        }
      });
    }
    handleArrowDown() {
      const items = this.getResultItems();
      if (items.length === 0)
        return;
      this.focusedIndex = (this.focusedIndex + 1) % items.length;
      this.updateFocusedItem(items);
    }
    handleArrowUp() {
      const items = this.getResultItems();
      if (items.length === 0)
        return;
      this.focusedIndex = this.focusedIndex <= 0 ? items.length - 1 : this.focusedIndex - 1;
      this.updateFocusedItem(items);
    }
    handleEnterKey() {
      const items = this.getResultItems();
      if (this.focusedIndex >= 0 && this.focusedIndex < items.length) {
        const focusedItem = items[this.focusedIndex];
        if (!focusedItem.classList.contains("is-disabled")) {
          focusedItem.click();
        }
      }
    }
    getResultItems() {
      return Array.from(this.resultsContainer.querySelectorAll("[data-variant-id]"));
    }
    updateFocusedItem(items) {
      items.forEach((item) => item.classList.remove("is-focused"));
      if (this.focusedIndex >= 0 && this.focusedIndex < items.length) {
        const focusedItem = items[this.focusedIndex];
        focusedItem.classList.add("is-focused");
        focusedItem.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
    handleClearClick() {
      this.input.value = this.prevSearchQuery;
      this.selectedItem = null;
      this.input.style.height = "auto";
      this.input.focus();
      this.setState("remove-selected");
      this.setState("show-results");
      this.callbacks.forEach((callback) => callback(null));
    }
    executeSearch() {
      if (this.platform === "coveo") {
        this.executeCoveoSearch();
      } else {
        this.executeNativeSearch();
      }
    }
    executeCoveoSearch() {
      try {
        const {
          engine: { dispatch },
          queryActions,
          searchActions
        } = this;
        dispatch(queryActions.updateQuery({ query: this.input.value }));
        dispatch(searchActions.executeSearch());
      } catch (error) {
        throw error;
      }
    }
    async executeNativeSearch() {
      const seq = ++this._renderSeq;
      if (!this.input.value.trim())
        return;
      this.setState("loading");
      try {
        const query = encodeURIComponent(this.input.value);
        const root = window.Shopify?.routes?.root || "/";
        const url = `${root}search/suggest.json?q=${query}&resources[type]=product&resources[limit]=10&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku`;
        const response = await fetch(url);
        if (!response.ok)
          throw new Error(`Search failed: ${response.status}`);
        const data = await response.json();
        this._nativeProducts = data?.resources?.results?.products || [];
        if (seq !== this._renderSeq || !this.input.value.trim())
          return;
        this.setAttribute("data-count", this._nativeProducts.length);
        this.classList.toggle("is-empty", this._nativeProducts.length === 0);
        const html = await this.fetchNativeProductCards(this._nativeProducts);
        if (seq !== this._renderSeq || !this.input.value.trim())
          return;
        this.resultsContainer.innerHTML = html;
        this.updateDisabledVariants();
        this.focusedIndex = -1;
        this.setState("show-results");
      } catch (error) {
        console.error("Native search error:", error);
        this.classList.remove("is-loading");
      }
    }
    async fetchNativeProductCards(products) {
      if (!products.length)
        return "";
      const ids = products.map((p) => p.id);
      const query = ids.map((id) => `id:${id}`).join(" OR ");
      const response = await fetch(`${theme.routes.searchUrl}?view=coveo-search-form&type=product&q=${query}`);
      if (!response.ok)
        throw new Error(`Failed to fetch product cards: ${response.status}`);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const items = [...doc.querySelectorAll("[data-product-id]")];
      const itemsById = new Map(items.map((item) => [item.dataset.productId, item]));
      const sorted = ids.map((id) => itemsById.get(String(id))).filter(Boolean);
      return sorted.map((el) => el.outerHTML).join("");
    }
    setState(state) {
      switch (state) {
        case "show-results":
          this.classList.remove("is-loading");
          this.classList.add("is-results", "is-open");
          break;
        case "loading":
          this.classList.remove("is-results");
          this.classList.add("is-loading");
          break;
        case "selected":
          this.classList.add("is-selected");
          this.classList.remove("is-results");
          this.input.readOnly = true;
          break;
        case "remove-selected":
          this.classList.remove("is-selected");
          this.input.readOnly = false;
          break;
        case "close":
          this.classList.remove("is-open");
          break;
        case "reset":
          this.classList.remove("is-selected", "is-results", "is-loading");
          this.input.readOnly = false;
          break;
      }
    }
    async renderProducts() {
      const seq = ++this._renderSeq;
      const { products } = this.search.state;
      if (!this.input.value.trim())
        return;
      this.setAttribute("data-count", products.length);
      this.classList.toggle("is-empty", products.length === 0);
      const html = await this.fetchProductCards(products);
      if (seq !== this._renderSeq || !this.input.value.trim())
        return;
      this.resultsContainer.innerHTML = html;
      this.updateDisabledVariants();
      this.focusedIndex = -1;
      this.setState("show-results");
    }
    fetchProductCards(products) {
      return fetchSortedProductCards(products);
    }
    handleResultClick(e) {
      const { target } = e;
      e.preventDefault();
      const parent = target.closest("[data-variant-id]");
      if (!parent)
        return;
      const { variantId, title } = parent.dataset;
      let foundItem;
      if (this.platform === "coveo") {
        foundItem = this.search.state.products.find(
          ({ ec_product_id }) => ec_product_id.split("/").pop() == variantId
        );
      } else {
        foundItem = this._resolveNativeProduct(variantId, parent.dataset);
      }
      this.setState("selected");
      this.setState("close");
      this.prevSearchQuery = this.input.value;
      this.selectedItem = foundItem;
      this.input.value = title;
      this.setInputHeight();
      this.callbacks.forEach((callback) => callback(foundItem));
    }
    _resolveNativeProduct(variantId, cardData) {
      for (const product of this._nativeProducts) {
        const variant = product.variants?.find((v) => String(v.id) === variantId);
        if (variant) {
          return {
            ec_product_id: `gid://shopify/ProductVariant/${variantId}`,
            ec_item_group_id: String(product.id),
            ec_name: product.title,
            additionalFields: {
              sku: variant.sku || cardData.sku || ""
            }
          };
        }
      }
      return {
        ec_product_id: `gid://shopify/ProductVariant/${variantId}`,
        ec_item_group_id: cardData.productId || "",
        ec_name: cardData.title || "",
        additionalFields: { sku: cardData.sku || "" }
      };
    }
    onSelect(callback) {
      this.callbacks.push(callback);
    }
    setDisabledVariants(variants) {
      for (const v of variants) {
        this.disabledVariants.add(String(v));
      }
      this.updateDisabledVariants();
    }
    updateDisabledVariants() {
      for (const child of this.resultsContainer.children) {
        const id = child.dataset.variantId;
        child.classList.toggle("is-disabled", this.disabledVariants.has(id));
      }
    }
    setInputHeight() {
      this.input.style.height = "auto";
      this.input.style.height = this.input.scrollHeight + "px";
    }
    clearSelectedItem() {
      this.selectedItem = null;
      this.focusedIndex = -1;
      this.input.value = "";
      this.input.style.height = "auto";
      this.setState("reset");
    }
    focus() {
      this.input.focus();
    }
  };
  customElements.define("product-selector-search", ProductSelectorSearch);

  // app/scripts/product-selector.js
  var ProductSelector = class extends HTMLElement {
    constructor() {
      super();
      this.quantity = this.querySelector("quantity-input");
      this.form = this.querySelector("form");
      this.callbacks = [];
      this.isLoading = false;
      this.searchBox = this.querySelector("product-selector-search");
    }
    connectedCallback() {
      this.form.addEventListener("submit", this.handleSubmit.bind(this));
      this.searchBox.onSelect(this.handleSelect.bind(this));
    }
    handleSelect(selectedVariant) {
      this.dataset.count = selectedVariant ? 1 : 0;
    }
    setState(state) {
      switch (state) {
        case "loading":
          this.classList.add("is-loading");
          break;
        case "selected":
          this.classList.add("is-selected");
          break;
        case "remove-selected":
          this.classList.remove("is-selected");
          this.selectedItem = null;
          break;
        default:
          this.classList.remove("is-loading");
          break;
      }
    }
    async handleSubmit(event) {
      try {
        event.preventDefault();
        if (this.isLoading)
          return;
        const selectedItemsValues = [this.searchBox.selectedItem];
        this.setState("loading");
        const PCProducts = await fetch(
          `${theme.routes.searchUrl}?view=pc-products&q=${selectedItemsValues.map((data) => `variants.sku:${data.additionalFields.sku}`).join(" OR ")}`
        ).then((res) => res.json());
        const selectedItems = selectedItemsValues.map((data) => {
          const { MultipleSaleQty = 1 } = PCProducts[data.ec_product_id.split("/").pop()] || {};
          return {
            ...data,
            quantity: this.quantity.value * MultipleSaleQty
          };
        });
        Promise.all(this.callbacks.map((cb) => cb(selectedItems))).finally(() => {
          this.setState();
          this.searchBox.clearSelectedItem();
          this.dataset.count = 0;
          this.quantity.value = 1;
          this.searchBox.focus();
        });
      } catch (error) {
        throw error;
      }
    }
    onAdd(cb) {
      this.callbacks.push(cb);
    }
    /**
     * @param {Array} variants - Include the variants ids to be disabled
     */
    setDisabledVariants(variantIds) {
      this.searchBox.setDisabledVariants(variantIds);
    }
  };
  customElements.define("product-selector", ProductSelector);
})();
